'use client'

import { type CSSProperties, useEffect, useState } from 'react'

interface VisualViewportRect {
  height: number
  offsetTop: number
  /** The layout viewport's height (`documentElement.clientHeight`), for the keyboard check. */
  layoutHeight: number
  /**
   * The on-screen keyboard is up: the visual viewport is well short of the layout viewport.
   * Only iOS Safari reports it — Android Chrome resizes the layout viewport with the keyboard
   * (`interactiveWidget: 'resizes-content'` in app/layout.tsx), so the two stay equal there.
   */
  keyboardOpen: boolean
}

// Taller than any browser-chrome wobble (iOS's collapsing toolbars move ~50-80px), shorter than
// any keyboard.
const KEYBOARD_MIN_HEIGHT_PX = 150

// Fields that raise the on-screen keyboard when focused.
const EDITABLE = 'input, textarea, select, [contenteditable="true"]'

/**
 * One reading of the visual viewport against the layout viewport. "Keyboard open" needs both a
 * big gap and a focused field: Safari's own toolbars can open a gap of their own (larger on
 * iOS 26's floating bars), and treating that as a keyboard pinned surfaces to the wrong box.
 */
export function readVisualViewport(viewport: VisualViewport): VisualViewportRect {
  const layoutHeight = document.documentElement.clientHeight
  const focused = document.activeElement
  const editableFocused = focused instanceof HTMLElement && focused.matches(EDITABLE)
  return {
    height: viewport.height,
    offsetTop: viewport.offsetTop,
    layoutHeight,
    keyboardOpen: editableFocused && layoutHeight - viewport.height > KEYBOARD_MIN_HEIGHT_PX,
  }
}

/**
 * Tracks `window.visualViewport`'s height/offsetTop. iOS Safari (and Android in some
 * configurations) only resizes the *visual* viewport when the on-screen keyboard opens —
 * the layout viewport (and anything sized off `inset-y-0`/100%/dvh) never changes — so a
 * `position: fixed` panel needs this to shrink itself above the keyboard instead of being
 * covered by it. Returns null until mounted or when the API is unavailable.
 */
export function useVisualViewportRect(): VisualViewportRect | null {
  const [rect, setRect] = useState<VisualViewportRect | null>(null)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const update = () => setRect(readVisualViewport(viewport))
    update()

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [])

  return rect
}

/**
 * App-wide, touch devices only: when the keyboard opens (or the surface holding the field is
 * resized around it) and the focused field ends up outside its scroll area, scroll it back into
 * view within its own scroller — instead of iOS panning the whole page and dragging fixed
 * headers/sheets out of place. Mounted once (provider/index.tsx).
 */
export function useKeepFocusedFieldVisible(enabled: boolean) {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!enabled || !viewport) return

    let frame = 0
    const onResize = () => {
      cancelAnimationFrame(frame)
      // A frame later: the surfaces resize on the same event, so measure after they have.
      frame = requestAnimationFrame(() => {
        const focused = document.activeElement
        if (!(focused instanceof HTMLElement) || !focused.matches(EDITABLE)) return
        const box = focused.getBoundingClientRect()
        const visibleBottom = viewport.offsetTop + viewport.height
        if (box.top < viewport.offsetTop || box.bottom > visibleBottom) {
          focused.scrollIntoView({ block: 'nearest' })
        }
      })
    }

    viewport.addEventListener('resize', onResize)
    return () => {
      viewport.removeEventListener('resize', onResize)
      cancelAnimationFrame(frame)
    }
  }, [enabled])
}

/**
 * Inline style for a full-height `position: fixed` mobile surface (a right-hand sheet, the
 * assistant chat shell) while the on-screen keyboard is up; undefined otherwise.
 *
 * Keyboard down, the surface's own stylesheet box (`top-0 bottom-…`) is right, and it must be
 * left alone: fixed edges follow Safari's collapsing toolbars natively, while a height tracked
 * from visualViewport in React lands a frame late on every scroll, so the bottom of the surface
 * (the Order Form's summary + submit) visibly lagged behind it.
 *
 * Keyboard up (iOS), the layout viewport doesn't shrink, so the box is pinned to the visual
 * viewport instead. Whatever fixed bar it normally stops above is behind the keyboard, so
 * nothing is reserved. The box also runs on to the layout viewport's bottom, with the overhang
 * as bottom padding: content still ends at the visible edge, but the surface's background fills
 * what lies between it and the keyboard — iOS 26's floating AutoFill bar sits outside the
 * visual viewport and otherwise showed the dashboard through around it.
 */
export function visualViewportFixedStyle(
  rect: VisualViewportRect | null
): CSSProperties | undefined {
  if (!rect?.keyboardOpen) return undefined
  const overhang = Math.max(0, rect.layoutHeight - rect.offsetTop - rect.height)
  return {
    top: rect.offsetTop,
    bottom: 'auto',
    height: rect.height + overhang,
    paddingBottom: overhang,
  }
}
